import {
  type AppState,
  type GetTokenSilentlyOptions,
  type RedirectLoginOptions,
} from "@auth0/auth0-react";
import { PostgrestClient } from "@supabase/postgrest-js";
import type { RefObject } from "react";
import type { DatabaseWithOptions } from "../../../__definitions__/database-definitions";
import type { Claims } from "./get-claims";
import type { SupabaseHandlerArg, SupabaseSchemaName } from "./types";

export const getSupabase = <
  DB extends { db: unknown; options: { PostgrestVersion: string } },
  SchemaName extends SupabaseSchemaName<DB["db"]>,
>(
  url: string,
  key: string,
  access_token: string,
  schema = "public" as SchemaName,
  abortControllerRef: RefObject<AbortController | null>,
): PostgrestClient<DB["db"], DB["options"], SchemaName> => {
  return new PostgrestClient(`${url}/rest/v1`, {
    headers: {
      apikey: key,
      Authorization: `Bearer ${access_token}`,
    },
    schema,
    fetch: (furl, init) => fetch(furl, { ...init, signal: abortControllerRef.current?.signal }),
  });
};

export async function withSupabaseToken<
  SchemaName extends SupabaseSchemaName<DatabaseWithOptions["db"]> = "public",
>(
  user: Claims | undefined,
  isLoading: boolean,
  isAuthenticated: boolean,
  getToken: (options?: GetTokenSilentlyOptions) => Promise<string>,
  loginWithRedirect: (options?: RedirectLoginOptions<AppState>) => Promise<void>,
  schema: SchemaName,
  abortControllerRef: RefObject<AbortController | null>,
): Promise<SupabaseHandlerArg<SchemaName> | undefined> {
  if (isLoading) return;
  if (!isAuthenticated) {
    await loginWithRedirect(
      window.location.pathname.includes("/logout")
        ? {}
        : {
            appState: {
              returnTo: `${window.location.pathname}${window.location.search}`,
            },
          },
    );
    return;
  }
  try {
    const token = await getToken();
    if (!user) return;
    return {
      supabase: getSupabase<DatabaseWithOptions, SchemaName>(
        user["http://changeengine.com/supabase_url"],
        user["http://changeengine.com/supabase_anon_key"],
        token,
        schema,
        abortControllerRef,
      ),
      account_id: user["http://changeengine.com/account_id"],
      org_id: user.org_id,
      token,
      abortController: abortControllerRef.current,
    };
  } catch {
    await loginWithRedirect(
      window.location.pathname.includes("/logout")
        ? {}
        : {
            appState: {
              returnTo: `${window.location.pathname}${window.location.search}`,
            },
          },
    );
  }
}
